import React, { useEffect, useState } from 'react';
import StepperInput from '../../../../StepperInput/StepperInput';
import ActionButtons from './ActionButtons';
import { useQueryBuilder } from '../context/QueryBuilderContext';
import { FILTER_OPERATORS } from '../../../utils/constants';

interface DimensionFilterDropdownProps {
  closeDropdown: () => void;
  
}

const DimensionFilterDropdown: React.FC<DimensionFilterDropdownProps> = ({ closeDropdown }) => {
  const [minWidth, setMinWidth] = useState<number | undefined>(undefined);
  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);
  const [minHeight, setMinHeight] = useState<number | undefined>(undefined);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const { setMultipleConditions, removeMultipleConditions, getConditionValue } = useQueryBuilder();

  const clearAll = () => {
    setMinWidth(undefined);
    setMaxWidth(undefined);
    setMinHeight(undefined);
    setMaxHeight(undefined);
  };

  const applyFilter = () => {
    if (
      minWidth === undefined &&
      maxWidth === undefined &&
      minHeight === undefined &&
      maxHeight === undefined
    ) {
      removeMultipleConditions(['dimensions.width', 'dimensions.height']);
      closeDropdown();
      return;
    }

    const conditions: { field: string; operator: string; value: any }[] = [];
    const fieldsToRemove: string[] = ['dimensions.width', 'dimensions.height'];

    const widthValue: Record<string, number> = {};
    if (minWidth !== undefined) widthValue.$gte = minWidth;
    if (maxWidth !== undefined) widthValue.$lte = maxWidth;

    if (Object.keys(widthValue).length > 0) {
      conditions.push({
        field: 'dimensions.width',
        operator: FILTER_OPERATORS.RANGE,
        value: widthValue,
      });
      fieldsToRemove.splice(fieldsToRemove.indexOf('dimensions.width'), 1);
    }

    const heightValue: Record<string, number> = {};
    if (minHeight !== undefined) heightValue.$gte = minHeight;
    if (maxHeight !== undefined) heightValue.$lte = maxHeight;

    if (Object.keys(heightValue).length > 0) {
      conditions.push({
        field: 'dimensions.height',
        operator: FILTER_OPERATORS.RANGE,
        value: heightValue,
      });
      fieldsToRemove.splice(fieldsToRemove.indexOf('dimensions.height'), 1);
    }

    if (fieldsToRemove.length > 0) {
      removeMultipleConditions(fieldsToRemove);
    }

    if (conditions.length > 0) {
      setMultipleConditions(conditions);
    }

    closeDropdown();
  };

  const handleWidthMin = (value: number | null) => setMinWidth(value ?? undefined);
  const handleWidthMax = (value: number | null) => setMaxWidth(value ?? undefined);
  const handleHeightMin = (value: number | null) => setMinHeight(value ?? undefined);
  const handleHeightMax = (value: number | null) => setMaxHeight(value ?? undefined);

  useEffect(() => {
    const widthCondition = getConditionValue('dimensions.width') as { $range?: { $gte?: number; $lte?: number } } | null;
    const heightCondition = getConditionValue('dimensions.height') as { $range?: { $gte?: number; $lte?: number } } | null;

    setMinWidth(widthCondition?.$range?.$gte ?? undefined);
    setMaxWidth(widthCondition?.$range?.$lte ?? undefined);

    setMinHeight(heightCondition?.$range?.$gte ?? undefined);
    setMaxHeight(heightCondition?.$range?.$lte ?? undefined);
  }, []);

  return (
    <>
      <div className="DimensionFilter">
        <div className="DimensionFilter__width-wrapper">
          <span className="DimensionFilter__label">{'Width (px)'}</span>
          <div className="DimensionFilter__inputs">
            <StepperInput
              value={minWidth}
              onChange={handleWidthMin}
              placeholder={'Min'}
              min={0}
              max={9999999}
              step={1}
              inputWidth="3.5rem"

            />
            <span className="DimensionFilter__separator">{'to'}</span>
            <StepperInput
              value={maxWidth}
              onChange={handleWidthMax}
              placeholder={'Max'}
              min={0}
              max={9999999}
              step={1}
              inputWidth="3.5rem"

            />
          </div>
        </div>

        <div className="DimensionFilter__height-wrapper">
          <span className="DimensionFilter__label">{'Height (px)'}</span>
          <div className="DimensionFilter__inputs">
            <StepperInput
              value={minHeight}
              onChange={handleHeightMin}
              placeholder={'Min'}
              min={0}
              max={9999999}
              step={1}
              inputWidth="3.5rem"

            />
            <span className="DimensionFilter__separator">{'to'}</span>
            <StepperInput
              value={maxHeight}
              onChange={handleHeightMax}
              placeholder={'Max'}
              min={0}
              max={9999999}
              step={1}
              inputWidth="3.5rem"

            />
          </div>
        </div>
      </div>

      <ActionButtons closeDropdown={closeDropdown} clearAll={clearAll} applyFilter={applyFilter} />
    </>
  );
};

export default DimensionFilterDropdown;
